import React, { useEffect, useContext, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Panel, Text, theme } from '@xometry/xometry_loft';
import { css } from '@emotion/react';
import queryString from 'query-string';
import { NotificationContext } from '../../contexts/Notification';
import UserProvider from '../../contexts/User/UserContext';
import Notification from '../Notification';
import UserBlock from '../User/UserBlock';
import ShippingAddressBlock from '../ShippingAddress';
import StaffSettings from '../StaffSettings';
import BillingProfiles from '../BillingProfiles';
import PaymentMethods from '../PaymentMethods';
import PunchoutHidden from '../PunchoutHidden';
import Teams from '../Teams';
import CTABanner from './CTABanner';
import AuthContext from '@xometry/auth-context';
import { mockUser } from '../../mocks/UserMocks';
import { entityTypeEnum } from '../ShippingAddress/types';
import PendingInvitesBanner from './PendingInvitesBanner';

const accountsettings_page = css`
  margin: 80px 8%;
  color: rgb(60, 60, 60);

  @media (max-width: 640px) {
    margin: 80px 16px;
  }
`;

const generalsettings_content = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[9]} ${theme.space[10]};

  @media (min-width: 1350px) {
    flex-direction: row;
  }
`;

const generalsettings_left = css`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 36px 0px;
`;

const generalsettings_right = css`
  flex: 1 0 auto;
  order: -1;

  @media (min-width: 1350px) {
    order: initial;
  }
`;

const notificationContainerCss = css`
  max-width: 792px;
  width: 100%;
`;

const Account = () => {
  const {
    notification,
    handleNotification,
    addNotification,
    checkNotifications,
  } = useContext(NotificationContext);
  const location = useLocation();
  const params = useParams();
  const { user, isStaff, refresh } = useContext(AuthContext);

  const userID = params.userid || user?.id || mockUser._id;
  const [pendingInvites, setPendingInvites] = useState([]);
  const [contentView, setContentView] = useState('');
  const [invitationModalData, setInvitationModalData] = useState({
    inviterFirstName: '',
    inviterLastName: '',
    teamName: '',
    openStatus: false,
    url: '',
    isStaffActingOnBehalfOfUser: isStaff && Boolean(params.userid),
    buttonDecision: '',
    userEmail: '',
    orgId: '',
    userName: 'user',
  });
  useEffect(() => {
    const queryMsg = queryString.parse(location.search);
    if (
      Object.keys(queryMsg).length > 0 &&
      'msg' in queryMsg &&
      !checkNotifications(queryMsg.type, queryMsg.msg)
    ) {
      addNotification(queryMsg.type, queryMsg.msg);
    }
    if (Object.keys(queryMsg).length > 0 && 'view' in queryMsg) {
      setContentView(queryMsg.view);
    }
  }, []);

  // If a hash is set in the url, go to the section.
  useEffect(() => {
    if (document.location.hash !== '') {
      setTimeout(() => {
        document
          .querySelector(document.location.hash)
          .scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 2000);
    }
  }, []);

  useEffect(() => {
    if (window.analytics && user) {
      window.analytics.identify({
        userId: userID,
        traits: {
          id: userID,
        },
      });

      window.analytics.track('User settings page viewed', {
        isStaff: isStaff(),
        userId: userID,
        email: user.emailAddress,
      });
    }
  }, [user]);

  return (
    <div css={accountsettings_page}>
      <div>
        {notification.length > 0 &&
          notification.map((notification, index) => (
            <div key={index} css={notificationContainerCss}>
              <Notification
                text={notification.text}
                type={notification.type}
                extraOptions={notification.extraOptions}
                handleClose={handleNotification}
                notificationIndex={index}
              />
            </div>
          ))}

        <UserProvider userId={userID}>
          <Box marginBottom={theme.space[9]}>
            <CTABanner />
            {pendingInvites && (
              <PendingInvitesBanner
                invites={pendingInvites}
                setContentView={setContentView}
                setInvitationModalData={setInvitationModalData}
                isStaff={isStaff && Boolean(params.userid)}
              />
            )}
          </Box>
          <div css={generalsettings_content}>
            <div css={generalsettings_left}>
              <Box maxWidth="792px" width="100%">
                <Text kind="subpage-title">General Account Settings</Text>
                <Panel kind="primary" marginTop={theme.space[4]}>
                  <UserBlock
                    userID={userID}
                    onUserUpdate={refresh}
                    staffMode={isStaff()}
                    displayNotification={addNotification}
                  />
                </Panel>
              </Box>
              <Teams
                userId={userID}
                isStaff={isStaff()}
                contentView={contentView}
                setPendingInvites={setPendingInvites}
                invitationModalData={invitationModalData}
                setInvitationModalData={setInvitationModalData}
              />
              <Box maxWidth="792px" width="100%">
                <Text kind="subpage-title">Shipping Addresses</Text>
                <ShippingAddressBlock
                  entityID={userID}
                  entityType={entityTypeEnum.user}
                  onShippingAddressUpdate={refresh}
                  displayNotification={addNotification}
                />
              </Box>
              <PunchoutHidden>
                <BillingProfiles userID={userID} isStaff={isStaff()} />
                {user && userID === user.id && (
                  <PaymentMethods userID={userID} />
                )}
              </PunchoutHidden>
            </div>
            {isStaff() && (
              <div css={generalsettings_right}>
                <StaffSettings
                  userID={userID}
                  displayNotification={addNotification}
                />
              </div>
            )}
          </div>
        </UserProvider>
      </div>
    </div>
  );
};

export default Account;
